<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else class="container">
    <div class="back-button-container">
      <main-button flat @click="goBack">
        <v-icon class="arrow-back">arrow_back</v-icon>
        <span>{{ adminText('organizationAll.back_to_all_promo_codes_99ac30d2', 'back to all promo codes') }}</span>
      </main-button>
    </div>
    <div class="container-header">
      <h1>{{ adminText('organizationAll.promo_code_9a041a7e', 'Promo code -') }} {{ promoCode.name }}</h1>
      <div class="header-buttons-container">
        <main-button outline @click="showEditModal = true">
          <v-icon width="24" height="24" style="margin-right: 0.5rem">edit</v-icon>
          {{ adminText('organizationAll.edit_5301648d', 'Edit') }}
        </main-button>
        <main-button color="error" outline @click="showDeletePromoCodeDialog = true">
          <v-icon width="24" height="24" style="margin-right: 0.5rem">delete</v-icon>
          {{ adminText('organizationAll.delete_f6fdbe48', 'Delete') }}
        </main-button>
      </div>
    </div>
    <div class="info-container">
      <div class="info-container-block text-block">
        <p>{{ adminText('organizationAll.unique_promo_code_72732bc6', 'Unique Promo code') }}</p>
        <h5 class="strong-text">{{ promoCode.code }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>{{ adminText('organizationAll.sold_quantity_56d296cc', 'Sold quantity') }}</p>
        <h5>{{ calculateTotalSoldQuantity() }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>{{ adminText('organizationAll.max_quantity_99edbe53', 'Max. quantity') }}</p>
        <h5>{{ calculateTotalInitialQuantity() }}</h5>
      </div>
      <div class="info-container-block text-block">
        <p>{{ adminText('organizationAll.max_tickets_per_order_68598ef3', 'Max. tickets per order') }}</p>
        <h5>{{ promoCode.ticketsPerOrder }}</h5>
      </div>
      <div class="info-container-block number-block">
        <p>{{ adminText('organizationAll.discount_b524936d', 'Discount') }}</p>
        <h5 class="strong-text">
          {{
            promoCode.discountPercentage
              ? `${promoCode.discountPercentage}%`
              : `${promoCode.discountAmount} ${promoCode.currency}`
          }}
        </h5>
      </div>
      <div class="info-container-block date-block">
        <p>{{ adminText('organizationAll.start_date_ff99f5b5', 'Start date') }}</p>
        <h5>{{ moment(promoCode.from).format('DD.MM.YYYY.') }}</h5>
      </div>
      <div class="info-container-block date-block">
        <p>{{ adminText('organizationAll.end_date_89d10cd6', 'End date') }}</p>
        <h5>{{ moment(promoCode.to).format('DD.MM.YYYY.') }}</h5>
      </div>
    </div>
    <h2 class="section-title">{{ adminText('organizationAll.applicable_events_4c9b0991', 'Applicable Events') }}</h2>
    <p class="section-description">
      {{ adminText('organizationAll.below_is_a_list_of_events_where_this_promo_co_766fe9cf', 'Below is a list of events where this promo code can be applied. Use it to quickly see all eligible events connected to the code.') }}
    </p>
    <div class="actions-header">
      <main-button :disabled="allEvents.length === 0" outline @click="showNewEventDialog = true">
        <v-icon class="edit-icon">add</v-icon>
        <span>{{ adminText('organizationAll.add_event_7f8a6fad', 'Add event') }}</span>
      </main-button>
      <v-text-field
        :label="adminText('organizationAll.search_event_41d376fc', 'Search Event')"
        append-icon="search"
        box
        style="max-width: 24.063rem"
        hide-details
        @change="handleSearchChange($event)"
      />
    </div>
    <div class="table-container">
      <selected-items-header
        v-if="selected.length"
        :selected="selected"
        name="event"
        @clear="selected = []"
        @delete="showDeleteEventPromoCodeDialog = true"
      />
      <table-list
        :selected.sync="selected"
        :organization-slug="organizationSlug"
        :attendees="orderItems"
        body-style="grid-auto-columns: 50px minmax(50px, 150px) !important;"
        :visible-fields="visibleFields"
        :attendee-fields="fields"
        :pagination="pagination"
        @sort="handleSort($event)"
      />
      <table-pagination
        :pagination="pagination"
        :total-attendees="totalOrderItemsCount"
        @change="handlePagination($event)"
        @changePerPage="handlePagination($event)"
      />
    </div>
    <delete-dialog
      :content="`${adminText('organizationAll.are_you_sure_you_want_to_delete_this_promo_co_834da9b3', 'Are you sure you want to delete this promo code? This operation cannot be undone.')}`"
      :on-delete="deletePromoCode"
      :show.sync="showDeletePromoCodeDialog"
      :title="adminText('organizationAll.delete_promo_code_5968992e', 'Delete promo code')"
    />
    <delete-dialog
      :content="`${adminText('organizationAll.are_you_sure_you_want_to_remove_selected_tick_7698d90d', 'Are you sure you want to remove selected tickets? This operation cannot be undone.')}`"
      :on-delete="deleteEventsPromoCode"
      :show.sync="showDeleteEventPromoCodeDialog"
      :title="adminText('organizationAll.remove_tickets_from_promo_code_08bbf2e7', 'Remove tickets from promo code')"
    />
    <v-dialog v-model="showNewEventDialog" max-width="512" persistent>
      <div class="modal-container">
        <div class="modal-header">
          <div class="modal-text-container">
            <h1 class="modal-text-title">{{ adminText('organizationAll.add_new_events_1ad4f7ba', 'Add new events') }}</h1>
          </div>
          <v-icon color="#979797" class="close-icon" @click="cancel">close</v-icon>
        </div>
        <div class="main-container">
          <div class="form-container">
            <select-field-multiple
              v-model="newEventForm.eventIds"
              :validator="$v.newEventForm.eventIds"
              :label="adminText('organizationAll.event_baf6e358', 'Event*')"
              :items="allEvents"
              item-render="title"
              item-select="id"
            />
            <select-field-multiple
              v-if="performances.length"
              v-model="newEventForm.performanceIds"
              :validator="$v.newEventForm.performanceIds"
              :error-messages="{
                selectedPerformances: adminText('organizationAll.please_select_performances_for_all_selected_e_c17478a9', 'Please select performances for all selected events'),
              }"
              :label="adminText('organizationAll.performance_e4aee885', 'Performance*')"
              :items="performances"
              item-render="title"
              item-select="id"
            />
            <select-field-multiple
              v-model="newEventForm.orderItemIds"
              :disabled="!newEventForm.eventIds.length"
              :validator="$v.newEventForm.orderItemIds"
              :error-messages="{
                selectedOrderItems: adminText('organizationAll.please_select_tickets_for_all_selected_events_7c58b70d', 'Please select tickets for all selected events'),
              }"
              :label="adminText('organizationAll.ticket_5be34f36', 'Ticket*')"
              :items="allOrderItems"
              item-render="name"
              item-select="id"
            />
          </div>
          <div class="button-container">
            <main-button @click="handleUpdate">{{ adminText('organizationAll.update_0a25ba59', 'update') }}</main-button>
          </div>
        </div>
      </div>
    </v-dialog>
    <edit-promo-code-modal
      v-if="showEditModal"
      :show.sync="showEditModal"
      :promo-code="promoCode"
      @save="handleEditPromoCode"
    />
    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import moment from 'moment'
import selectedItemsHeader from '../../../components/tickets/selected-items-header.vue'
import deleteDialog from '../../shared/dialogs/delete-dialog.vue'
import tableList from '../../accreditations/attendee-table/list.vue'
import {
  GET_PROMO_CODE,
  REMOVE_TICKETS_FROM_PROMO_CODE,
  GET_PROMO_CODES,
  ADD_TICKETS_TO_PROMO_CODE,
  DELETE_PROMO_CODE,
  UPDATE_PROMO_CODE,
} from '../../../graphql/promoCode.graphql'
import EditPromoCodeModal from './EditPromoCodeModal.vue'
import { GET_EVENTS } from '../../../graphql/event.graphql'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { compact, difference, intersection, isEqual, uniq, uniqBy } from 'lodash'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import tablePagination from '@/components/accreditations/attendee-table/pagination.vue'

const discountTypes = Object.freeze({
  PERCENTAGE: 'Discount percentage',
  AMOUNT: 'Discount amount',
})

const selectedPerformances = (eventTemplates, allPerformances) => (val, model) => {
  const selectedEventTemplatesIds = eventTemplates
    .filter(et => model.eventIds.includes(et.id))
    .map(et => et.id)

  const selectedPerformanceEventTemplatesIds = uniq(
    allPerformances.filter(p => val.includes(p.id)).map(p => p.eventTemplate.id),
  )

  return isEqual(selectedEventTemplatesIds, selectedPerformanceEventTemplatesIds)
}

const selectedOrderItems = (eventsWithoutTemplate, allPerformances) => (val, model) => {
  const allEvents = [...eventsWithoutTemplate, ...allPerformances]

  const selectedEventsIds = [
    ...model.eventIds.filter(ei => allEvents.map(e => e.id).includes(ei)),
    ...model.performanceIds,
  ].sort()

  const selectedOrderItemsEventsIds = allEvents
    .filter(e => e.orderItems.map(oi => oi.id).some(el => val.includes(el)))
    .map(e => e.id)
    .sort()

  return isEqual(selectedOrderItemsEventsIds, selectedEventsIds)
}

export default {
  mixins: [OrganizationLocalization],
  name: 'promo-code-info-container',
  components: {
    tablePagination,
    selectedItemsHeader,
    tableList,
    deleteDialog,
    SimpleSnackbar,
    EditPromoCodeModal,
  },
  props: {
    organizationSlug: String,
    promoCodeId: [Number, String],
  },
  data() {
    const { pagination } = this.getInitialFromQueryParams()

    return {
      selected: [],
      showDeletePromoCodeDialog: false,
      showDeleteEventPromoCodeDialog: false,
      showNewEventDialog: false,
      showEditModal: false,
      promoCode: null,
      orderItems: [],
      fields: {},
      visibleFields: ['Event name', 'Date', 'Ticket name', 'Sold quantity'],
      snackbar: {},
      pagination,
      moment,
      discountTypes,
      newEventForm: { eventIds: [], performanceIds: [], orderItemIds: [] },
      allEvents: [],
      eventTemplates: [],
      eventsWithoutTemplate: [],
      allPerformances: [],
      searchTerm: null,
      totalOrderItemsCount: 0,
    }
  },
  validations() {
    return {
      newEventForm: {
        eventIds: { required },
        performanceIds: {
          required: requiredIf(
            val =>
              intersection(
                this.eventTemplates.map(et => et.id),
                val.eventIds,
              ).length,
          ),
          selectedPerformances: selectedPerformances(this.eventTemplates, this.allPerformances),
        },
        orderItemIds: {
          required,
          selectedOrderItems: selectedOrderItems(this.eventsWithoutTemplate, this.allPerformances),
        },
      },
    }
  },
  apollo: {
    getPromoCode: {
      query: GET_PROMO_CODE,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCodeId),
          pagination: {
            ...this.pagination,
            per_page: this.pagination.per_page === 'All' ? -1 : this.pagination.per_page,
            sortBy: this.transformSortByString(this.pagination.sortBy),
          },
          searchTerm: this.searchTerm,
        }
      },
      async result({ data: { getPromoCode }, loading }) {
        if (!loading) {
          if (!getPromoCode) {
            return this.goBack()
          }

          this.promoCode = getPromoCode.promoCode
          this.orderItems = getPromoCode.promoCode.orderItems.map(oi => ({
            id: oi.id,
            'event name': oi.event.title,
            date: moment(oi.event.from).format('DD.MM.YY HH:mm'),
            name: oi.name,
            quantity: oi.OrderItemPromoCode.quantity ? oi.OrderItemPromoCode.quantity : '-',
            'initial quantity': oi.OrderItemPromoCode.initialQuantity
              ? oi.OrderItemPromoCode.initialQuantity
              : '-',
            'sold quantity': oi.OrderItemPromoCode.soldQuantity || 0,
            unlimited: oi.OrderItemPromoCode.unlimited ? 'Yes' : 'No',
          }))

          this.totalOrderItemsCount = getPromoCode.count

          const {
            data: { getEvents },
          } = await this.$apollo.query({
            query: GET_EVENTS,
            variables: { organizationSlug: this.organizationSlug },
          })

          if (!getEvents) {
            this.goBack()
          } else {
            const liveEvents = getEvents
              .map(e => ({
                ...e,
                orderItems: (e.orderItems || []).filter(oi => !(oi.itemMeta && oi.itemMeta.isGratis)),
              }))
              .filter(
                e =>
                  moment(e.to).isAfter(moment()) &&
                  difference(
                    e.orderItems.map(oi => oi.id),
                    this.promoCode.orderItems.filter(oi => oi.event.id === e.id).map(oi => oi.id),
                  ).length > 0,
              )
            const eventsWithoutTemplate = liveEvents.filter(e => !e.eventTemplate)
            const eventTemplates = uniqBy(compact(liveEvents.map(e => e.eventTemplate)), 'id')

            this.allEvents = [...eventsWithoutTemplate, ...eventTemplates]
            this.eventsWithoutTemplate = eventsWithoutTemplate
            this.eventTemplates = eventTemplates
            this.allPerformances = liveEvents.filter(e => e.eventTemplate)
          }
        }
      },
    },
  },
  computed: {
    performances() {
      const newPerformances = this.allPerformances
        .filter(p => this.newEventForm.eventIds.includes(p.eventTemplate.id))
        .map(p => ({
          ...p,
          title: `${p.eventTemplate.title} - ${moment(p.from).format('DD/MM/YYYY. HH:mm')}`,
        }))

      if (newPerformances.length === 0) {
        this.$v.newEventForm.performanceIds.$reset()
      }

      return newPerformances
    },
    allOrderItems() {
      return compact(
        [...this.allEvents, ...this.allPerformances]
          .filter(e =>
            [
              ...this.newEventForm.eventIds,
              ...(this.newEventForm.performanceIds ? this.newEventForm.performanceIds : []),
            ].includes(e.id),
          )
          .flatMap(e =>
            e.orderItems
              ? e.orderItems
                .filter(oi => !this.orderItems.map(oi => oi.id).includes(oi.id))
                .map(oi => ({
                  ...oi,
                  name: `${e.title}${
                    e.eventTemplate ? ` (${moment(e.from).format('DD/MM/YYYY. HH:mm')})` : ''
                  } - ${oi.name}`,
                }))
              : [],
          ),
      )
    },
  },
  watch: {
    newEventForm: {
      handler(val) {
        const newForm = { ...val }

        newForm.performanceIds = intersection(
          newForm.performanceIds,
          this.performances
            .filter(p => newForm.eventIds.includes(p.eventTemplate.id))
            .map(p => p.id),
        )
        newForm.orderItemIds = intersection(
          newForm.orderItemIds,
          this.allOrderItems
            .filter(oi =>
              [
                ...newForm.eventIds,
                ...(newForm.performanceIds ? newForm.performanceIds : []),
              ].includes(oi.eventId),
            )
            .map(p => p.id),
        )
        if (!isEqual(this.newEventForm, newForm)) {
          this.newEventForm = newForm
        }
      },
      deep: true,
    },
  },
  methods: {
    cancel() {
      this.$v.$reset()
      this.newEventForm = { eventIds: [], performanceIds: [], orderItemIds: [] }
      this.showNewEventDialog = false
    },
    async handleUpdate() {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        const { errors } = await this.$apollo.mutate({
          mutation: ADD_TICKETS_TO_PROMO_CODE,
          variables: {
            organizationSlug: this.organizationSlug,
            promoCodeId: this.promoCode.id,
            orderItemIds: this.newEventForm.orderItemIds.map(id => parseInt(id)),
          },
          awaitRefetchQueries: true,
          refetchQueries: ['GetPromoCode'],
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: 'Oops, something went wrong!',
            color: 'error',
          }
        } else {
          this.snackbar = {
            open: true,
            messageProp: 'Successfully created promo code!',
            color: 'success',
          }

          this.showNewEventDialog = false
          this.$v.$reset()
          this.newEventForm = {
            eventIds: [],
            performanceIds: [],
            orderItemIds: [],
          }
        }
      }
    },
    async handleEditPromoCode(form) {
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_PROMO_CODE,
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeId: this.promoCode.id,
          input: form,
        },
        awaitRefetchQueries: true,
        refetchQueries: ['GetPromoCode'],
      })

      if (errors) {
        const message =
          errors.length && errors[0].message
            ? errors[0].message
            : 'Oops, something went wrong!'
        this.snackbar = {
          open: true,
          messageProp: message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully updated promo code!',
          color: 'success',
        }
        this.showEditModal = false
      }
    },
    async handlePagination({ page, per_page }) {
      const { page: currentPage, per_page: currentPerPage } = this.pagination
      if (currentPage > page && currentPage === 1) return
      if (page > currentPage && !(this.totalOrderItemsCount - currentPerPage * currentPage > 0))
        return

      this.pagination = {
        ...this.pagination,
        per_page,
        page,
      }

      this.addPaginationToRoute(this.pagination)
    },
    async handleSort(sortBy) {
      const { per_page, page, sortBy: thisSortBy, descending } = this.pagination
      const pagination = {
        page,
        per_page,
        sortBy,
        descending: sortBy === thisSortBy ? !descending : false,
      }
      this.addPaginationToRoute(pagination)
      this.pagination = pagination
    },
    getInitialFromQueryParams() {
      const { per_page, page, sortBy, descending } = this.$route.query

      return {
        pagination: {
          page: parseInt(page, 10) || 1,
          per_page: parseInt(per_page, 10) || 'All',
          sortBy: sortBy,
          descending: descending ? !!descending : false,
        },
      }
    },
    async addPaginationToRoute({ page, per_page, sortBy, descending }) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          per_page: parseInt(per_page, 10) || 'All',
          page,
          sortBy,
          descending: descending ? 1 : 0,
        },
      })
    },
    async deletePromoCode() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_PROMO_CODE,
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCode.id),
        },
      })

      if (errors) {
        if (errors.length && errors[0].message) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
        } else {
          this.snackbar = {
            open: true,
            messageProp: 'Oops, something went wrong!',
            color: 'error',
          }
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted promo code!',
          color: 'success',
        }
        this.goBack()
      }
    },
    async deleteEventsPromoCode() {
      const { data, errors } = await this.$apollo.mutate({
        mutation: REMOVE_TICKETS_FROM_PROMO_CODE,
        variables: {
          organizationSlug: this.organizationSlug,
          promoCodeId: parseInt(this.promoCode.id),
          orderItemIds: this.selected.map(id => parseInt(id)),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully removed event from promo code!',
          color: 'success',
        }
      }
      if (data.removeTicketsFromPromoCode && this.orderItems.length > this.selected.length) {
        await this.$apollo.queries.getPromoCode.refetch()
      } else {
        this.goBack()
      }
      this.selected = []
    },
    calculateTotalSoldQuantity() {
      return this.orderItems.reduce((sum, curr) => {
        return sum + curr['sold quantity'] || 0
      }, 0)
    },
    calculateTotalInitialQuantity() {
      if (this.orderItems.some(item => item.unlimited === 'Yes')) {
        return 'unlimited'
      }

      return this.orderItems.reduce((sum, curr) => {
        return sum + curr['initial quantity'] || 0
      }, 0)
    },
    transformSortByString(sortBy) {
      if (!sortBy) {
        return undefined
      }

      return sortBy === 'Event name'
        ? 'title'
        : sortBy === 'Date'
          ? 'from'
          : sortBy === 'Ticket name'
            ? 'name'
            : sortBy.slice(0, 1).toLowerCase() + sortBy.slice(1)
    },
    async handleSearchChange(input) {
      this.searchTerm = input
      await this.$apollo.queries.getPromoCode.refetch()
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 68rem;
}
.container-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  align-items: center;
  gap: 1rem;
}
.container-header h1 {
  color: #ff4c00;
  font-size: 24px;
  font-family: Raleway;
  font-weight: 600;
  line-height: 28.8px;
  word-wrap: break-word;
  align-content: center;
}
.header-buttons-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  justify-content: center;
}
.back-button-container {
  display: flex;
  align-items: center;
}
.info-container {
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  padding: 16px;
  gap: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: #f5f5f5;
  flex-wrap: wrap;
}
.info-container-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.text-block {
  flex: 1 0 0;
}
.date-block {
  width: 108px;
}
.number-block {
  width: 65px;
}
.info-container-block p {
  font-size: 14px;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  color: #666666;
  margin: 0%;
  padding: 0%;
}
.info-container-block h5 {
  font-size: 18px;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: #666666;
  margin: 0%;
  padding: 0%;
}
.strong-text {
  font-weight: 700 !important;
}
.table-container {
  margin-top: 16px;
}
.actions-header {
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.edit-icon {
  margin-right: 0.5rem;
}
.modal-container {
  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(51, 51, 51, 0.2);
  border-radius: 12px;
  padding: 24px 24px 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.modal-header {
  display: flex;
  align-items: baseline;
}
.modal-text-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.modal-text-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}
.close-icon {
  cursor: pointer;
  user-select: none;
}
.main-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.form-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0%;
}
.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
}
.section-title {
  color: var(--Secondary-00, #ff4c00);
  font-variant-numeric: lining-nums proportional-nums;

  /* Heading/H5 */
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 1.5rem */
  letter-spacing: -0.0375rem;
  margin-top: 1.5rem;
}
.section-description {
  color: var(--Grey-20, #979797);

  /* Body/Small */
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 1.5rem */
  margin-top: 1rem;
}
</style>
